
if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.modalTitle = this.querySelector('.quick-add-modal__title');
        this.defaultModalTitle = this.modalTitle ? this.modalTitle.textContent.trim() : '';
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.resetDrawerTitle();
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        this.resetDrawerTitle();
        const openerTitle = this.getOpenerProductTitle(opener);

        const spinner = opener.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const baseUrl = opener.getAttribute('data-product-url');
        const url = new URL(baseUrl, window.location.origin);

        fetch(url.toString())
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

            this.productElement = this.buildDrawerProduct(responseHTML, baseUrl, openerTitle);

            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.outerHTML || this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();

            this.preventVariantURLSwitching();
            this.resetDrawerScroll();

            super.show(opener);
          })
          .catch((err) => {
            console.error('[QuickAdd] Failed to load product section:', err);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      resetDrawerTitle() {
        if (!this.modalTitle) return;
        this.modalTitle.textContent = this.defaultModalTitle || 'Select options';
      }

      setDrawerTitle(title) {
        if (!this.modalTitle || !title) return;
        this.modalTitle.textContent = title;
      }

      getOpenerProductTitle(opener) {
        if (!opener) return '';

        const labelledBy = opener.getAttribute('aria-labelledby') || '';
        const labelIds = labelledBy.split(' ').filter(Boolean);
        const preferredIds = [
          ...labelIds.filter((id) => id.startsWith('title-')),
          ...labelIds.filter((id) => !id.startsWith('title-')),
        ];

        for (const id of preferredIds) {
          if (opener.id && id === opener.id) continue;
          const labelEl = document.getElementById(id);
          const labelText = labelEl && labelEl.textContent ? labelEl.textContent.trim() : '';
          if (!labelText) continue;
          if (labelText.toLowerCase() === 'choose options') continue;
          return labelText;
        }

        const card = opener.closest('.card-wrapper, .card, li, .grid__item');
        const titleEl = card && card.querySelector('[id^="title-"], .card__heading');
        return titleEl && titleEl.textContent ? titleEl.textContent.trim() : '';
      }

      preventVariantURLSwitching() {
        this.modalContent.querySelectorAll('variant-radios, variant-selects').forEach((variantPicker) => {
          variantPicker.setAttribute('data-update-url', 'false');
        });
      }

      removeDOMElements() {
        this.productElement.querySelectorAll('product-modal, [id^="ProductModal-"]').forEach((modal) => modal.remove());
      }

      buildDrawerProduct(responseHTML, productUrl, fallbackTitle = '') {
        const productSection =
          responseHTML.querySelector('section[id^="MainProduct-"]') ||
          responseHTML.querySelector('section.shopify-section') ||
          responseHTML.body;

        const sourceProduct = productSection.querySelector('.product');
        const titleBlock = productSection.querySelector('.product__title');
        const titleSource = titleBlock ? titleBlock.querySelector('h1, h2') : null;

        if (!sourceProduct) {
          const fallback = document.createElement('div');
          fallback.className = 'product product--quick-add-mobile';
          fallback.innerHTML = `<p><a href="${productUrl || '#'}">View full details</a></p>`;
          return fallback;
        }

        const drawerProduct = document.createElement('div');
        drawerProduct.className = 'quick-add-product';

        const productClone = sourceProduct.cloneNode(true);
        productClone.classList.add('product--quick-add');
        this.prepareProductMarkup(productClone);
        drawerProduct.appendChild(productClone);

        productSection
          .querySelectorAll('.product-popup-modal')
          .forEach((popupModal) => drawerProduct.appendChild(popupModal.cloneNode(true)));

        return drawerProduct;
      }

      prepareProductMarkup(productElement) {
        const directMediaWrappers = Array.from(productElement.children).filter((child) =>
          child.classList && child.classList.contains('product__media-wrapper')
        );

        if (directMediaWrappers.length > 1) {
          directMediaWrappers.slice(1).forEach((wrapper) => wrapper.remove());
        }

        productElement
          .querySelectorAll('.product__media-wrapper, .scroll-trigger, .animate--slide-in, .animate--fade-in, .scroll-trigger--offscreen')
          .forEach((element) => {
            element.classList.remove('small-hide', 'medium-hide', 'large-up-hide');
            element.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
        });

        productElement.querySelectorAll('.no-js-hidden').forEach((element) => {
          if (element.closest('.product-form__noscript-wrapper')) return;
          element.classList.remove('no-js-hidden');
        });
      }

      preventDuplicatedIDs() {
        const sectionId =
          this.productElement.querySelector('[data-section]')?.dataset.section ||
          this.productElement.querySelector('section[id^="MainProduct-"]')?.dataset.section ||
          '';
        if (!sectionId) return; // nothing to rewrite

        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
          sectionId,
          `quickadd-${sectionId}`
        );
        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        this.modalContent.querySelectorAll('[id^="Slider-Gallery"]').forEach((galleryList) => {
          galleryList.setAttribute('role', 'presentation');
          galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
        });
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product && product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }
        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      resetDrawerScroll() {
        const dialog = this.querySelector('[role="dialog"]');
        if (dialog) dialog.scrollTop = 0;
        if (this.modalContent) this.modalContent.scrollTop = 0;
      }
    }
  );
}
